import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Year-wise Engagement Chart
 * Grouped bar chart showing Total Visits and Active Users by year
 */
const MBFYearEngagementChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default data matching the Figma design
    const defaultData = [
        { year: '1st Year', total_visits: 3500, active_users: 450 },
        { year: '2nd Year', total_visits: 2400, active_users: 650 },
        { year: '3rd Year', total_visits: 1500, active_users: 400 },
        { year: '4th Year', total_visits: 2200, active_users: 700 },
    ];

    // Transform API data - API returns: [{ year: 'First Year', total_visits: 0, active_users: 0 }, ...]
    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.year || item.label);
    const totalVisits = chartData.map(item => item.total_visits || item.totalVisits || item.visits || 0);
    const activeUsers = chartData.map(item => item.active_users || item.activeUsers || item.users || 0);

    // Calculate dynamic max for Y-axis
    const allValues = [...totalVisits, ...activeUsers];
    const maxValue = Math.max(...allValues) || 100;
    const yAxisMax = Math.ceil(maxValue * 1.2 / 500) * 500 || 1000;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;
            const barThickness = isMobile ? 20 : 30;

            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Total Visits',
                            data: totalVisits,
                            backgroundColor: '#d0d0d0',
                            borderColor: '#b0b0b0',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: barThickness,
                        },
                        {
                            label: 'Active Users',
                            data: activeUsers,
                            backgroundColor: '#a8e6a8',
                            borderColor: '#7dd87d',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: barThickness,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            align: 'end',
                            labels: {
                                boxWidth: 12,
                                boxHeight: 12,
                                padding: 15,
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 11,
                                },
                                color: '#333333',
                            },
                        },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                        },
                        datalabels: { display: false },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            title: {
                                display: true,
                                text: 'Years',
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 12,
                                    weight: '500',
                                },
                            },
                        },
                        y: {
                            min: 0,
                            suggestedMax: yAxisMax,
                            ticks: {
                                color: '#666666',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                                callback: (value) => value.toLocaleString(),
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                            title: {
                                display: true,
                                text: 'No. of Users',
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 12,
                                    weight: '500',
                                },
                            },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, chartData, labels, totalVisits, activeUsers, yAxisMax]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Year-wise Engagement
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Chart Area */}
            <Box
                sx={{
                    flex: 1,
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    bgcolor: '#fafafa',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '16px',
                        minHeight: 0,
                    }}
                >
                    <canvas ref={chartRef} />
                </Box>
            </Box>
        </Box>
    );
};

export default MBFYearEngagementChart;
